// ─── src/DoorCodesView.tsx ───────────────────────────────────────────────────
//
// Every door code for scan.startupsyria.org, plus staff codes that admit
// tickets for all events (for the Startup Syria team on busy days).

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Loader2, Plus, ShieldCheck } from "lucide-react";
import {
  adminDoorCodesApi,
  doorCodeError,
  doorCodeMessage,
  type DoorScope,
} from "./api/doorCodes";
import { DoorCodeActions, formatWhen, useCopy } from "./components/dashboard/DoorCodesPanel";

const KEY = ["door-codes", "all"];

const SCOPE_LABEL: Record<DoorScope, string> = {
  all: "Staff · all events",
  gew_event: "GEW event",
  meetup: "Meetup",
};

function today() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Damascus" }).format(new Date());
}

export default function DoorCodesView() {
  const qc = useQueryClient();
  const { copied, copy } = useCopy();
  const [label, setLabel] = useState("");
  const [validUntil, setValidUntil] = useState(today());
  const [filter, setFilter] = useState<"active" | DoorScope | "inactive">("active");

  const { data: codes, isLoading } = useQuery({ queryKey: KEY, queryFn: () => adminDoorCodesApi.list() });

  const create = useMutation({
    mutationFn: () => adminDoorCodesApi.createStaff(label.trim() || undefined, validUntil),
    onSuccess: () => {
      setLabel("");
      qc.invalidateQueries({ queryKey: ["door-codes"] });
    },
  });
  const revoke = useMutation({
    mutationFn: adminDoorCodesApi.revoke,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["door-codes"] }),
  });

  const shown = useMemo(() => {
    const all = codes ?? [];
    if (filter === "active") return all.filter((c) => c.active);
    if (filter === "inactive") return all.filter((c) => !c.active);
    return all.filter((c) => c.scope === filter && c.active);
  }, [codes, filter]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <KeyRound size={22} className="text-brand-green" /> Door Codes
        </h1>
        <p className="text-brand-muted text-sm mt-1 max-w-2xl">
          Codes unlock the door scanner at <span className="font-mono">scan.startupsyria.org</span>.
          Partners create codes for their GEW events in the partner portal, catalysts for their
          meetups in the catalyst portal. Staff codes below admit tickets for every event.
        </p>
      </div>

      <form
        className="card p-4 space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <p className="text-white text-sm font-semibold flex items-center gap-2">
          <ShieldCheck size={15} className="text-brand-green" /> New staff code
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex-1 min-w-[200px] text-xs text-brand-muted">
            Note
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={120}
              placeholder="e.g. Startup Syria team — GEW week"
              className="mt-1 w-full bg-brand-navy border border-brand-border rounded-lg px-3 py-2 text-sm text-white placeholder:text-brand-muted focus:outline-none focus:border-brand-green"
            />
          </label>
          <label className="text-xs text-brand-muted">
            Valid until (end of day, Damascus)
            <input
              type="date"
              value={validUntil}
              min={today()}
              onChange={(e) => setValidUntil(e.target.value)}
              className="mt-1 block bg-brand-navy border border-brand-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-green [color-scheme:dark]"
            />
          </label>
          <button type="submit" disabled={create.isPending} className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-50">
            {create.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create
          </button>
        </div>
        <p className="text-xs text-brand-muted">
          A staff code admits any accepted ticket for any event, and its name search covers every
          event happening today. Share it only with the Startup Syria team; at most 31 days.
        </p>
        {create.isError && <p className="text-xs text-red-400">{doorCodeError(create.error)}</p>}
      </form>

      <div className="flex flex-wrap gap-2">
        {(
          [
            ["active", "Active"],
            ["all", "Staff"],
            ["gew_event", "GEW events"],
            ["meetup", "Meetups"],
            ["inactive", "Expired / revoked"],
          ] as const
        ).map(([key, text]) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              filter === key
                ? "bg-brand-green text-gray-900 border-brand-green font-semibold"
                : "border-brand-border text-brand-muted hover:text-white"
            }`}
          >
            {text}
          </button>
        ))}
      </div>

      <div className="card overflow-x-auto">
        {isLoading ? (
          <p className="p-4 text-sm text-brand-muted">Loading…</p>
        ) : shown.length === 0 ? (
          <p className="p-4 text-sm text-brand-muted">No codes here.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-brand-muted border-b border-brand-border">
                <th className="p-3 font-medium">Code</th>
                <th className="p-3 font-medium">For</th>
                <th className="p-3 font-medium">Created by</th>
                <th className="p-3 font-medium">Expires</th>
                <th className="p-3 font-medium">Last used</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-border">
              {shown.map((c) => (
                <tr key={c.id} className="align-top">
                  <td className={`p-3 font-mono font-bold tracking-[0.12em] whitespace-nowrap ${c.active ? "text-white" : "text-brand-muted line-through"}`}>
                    {c.code}
                  </td>
                  <td className="p-3">
                    <p className="text-white">{c.scope === "all" ? c.label || "Staff code" : c.eventTitle || "—"}</p>
                    <p className="text-xs text-brand-muted">
                      {SCOPE_LABEL[c.scope]}
                      {c.scope !== "all" && c.label && <> · {c.label}</>}
                    </p>
                  </td>
                  <td className="p-3 text-xs text-brand-muted">{c.createdBy}</td>
                  <td className="p-3 text-xs text-brand-muted whitespace-nowrap">
                    {c.revokedAt ? "Revoked" : formatWhen(c.expiresAt)}
                  </td>
                  <td className="p-3 text-xs text-brand-muted whitespace-nowrap">
                    {c.lastUsedAt ? formatWhen(c.lastUsedAt) : "—"}
                  </td>
                  <td className="p-3">
                    {c.active && (
                      <DoorCodeActions
                        code={c}
                        copied={copied === c.id}
                        onCopy={() => copy(c.id, doorCodeMessage(c))}
                        onRevoke={() => revoke.mutate(c.id)}
                        revoking={revoke.isPending}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
